import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_ORDER, EXCEL_STATUSES, APP_STATUSES, statusInfo } from "@/constants/statuses";
import MapView from "@/components/MapView";
import MeterPanel from "@/components/MeterPanel";
import MeterTable from "@/components/MeterTable";
import StatsView from "@/components/StatsView";
import ImportModal from "@/components/ImportModal";
import Footer from "@/components/Footer";
import { exportMetersToExcel } from "@/lib/exportExcel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Zap,
  Map as MapIcon,
  Table2,
  BarChart3,
  UploadCloud,
  LogOut,
  Search,
  Loader2,
  ChevronDown,
  Trash2,
  Settings,
  Radar,
  Download,
} from "lucide-react";

const TABS = [
  { key: "map", label: "Carte", icon: MapIcon },
  { key: "table", label: "Tableau", icon: Table2 },
  { key: "stats", label: "Statistiques", icon: BarChart3 },
];

function StatusFilterDropdown({ label, keys, visible, onToggle, counts, testid }) {
  const selected = keys.filter((k) => visible[k]).length;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid={testid} className="h-9 bg-slate-950/60 border-slate-700 text-xs text-slate-200 gap-1.5">
          {label}
          <span className="font-mono-data text-slate-400">({selected}/{keys.length})</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="z-[1200] bg-slate-900 border-slate-700 w-60">
        <DropdownMenuLabel className="text-xs text-slate-400">{label} — afficher</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-800" />
        {keys.map((k) => {
          const info = statusInfo(k);
          return (
            <DropdownMenuCheckboxItem
              key={k}
              checked={visible[k]}
              onCheckedChange={() => onToggle(k)}
              onSelect={(e) => e.preventDefault()}
              data-testid={`status-toggle-${k}`}
              className="text-sm text-slate-200 focus:bg-slate-800"
            >
              <span className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: info.color }} />
                {info.label}
                <span className="text-slate-500 font-mono-data text-xs">({counts[k] || 0})</span>
              </span>
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("map");
  const [visible, setVisible] = useState(() => {
    const v = {};
    STATUS_ORDER.forEach((k) => (v[k] = true));
    return v;
  });
  const [search, setSearch] = useState("");
  const [selection, setSelection] = useState(null);
  const [importMode, setImportMode] = useState(null); // "import" | "status"
  const [pending, setPending] = useState(0);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleRegeocode = async () => {
    try {
      const res = await api.post("/meters/regeocode");
      toast.success(`Re-scan lancé pour ${res.data.queued} compteur(s). Les positions se corrigent en arrière-plan (quelques minutes).`);
    } catch (e) {
      toast.error("Échec du re-scan des positions");
    }
  };

  const handleReset = async () => {    setResetting(true);
    try {
      const res = await api.delete("/meters/all");
      toast.success(`${res.data.deleted} compteur(s) supprimé(s)`);
      setSelection(null);
      await fetchMeters();
    } catch (e) {
      toast.error("Échec de la réinitialisation");
    } finally {
      setResetting(false);
      setResetOpen(false);
    }
  };

  const fetchMeters = useCallback(async () => {
    try {
      const res = await api.get("/meters");
      setMeters(res.data);
    } catch (e) {
      toast.error("Impossible de charger les compteurs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeters();
  }, [fetchMeters]);

  useEffect(() => {
    const prevPending = { current: null };
    const lastFetch = { current: 0 };
    const id = setInterval(async () => {
      try {
        const res = await api.get("/meters/import-status");
        const p = res.data.pending;
        setPending(p);
        const now = Date.now();
        const justFinished = prevPending.current !== null && p === 0 && prevPending.current > 0;
        const throttledRefresh = p > 0 && now - lastFetch.current > 30000;
        if (justFinished || throttledRefresh) {
          lastFetch.current = now;
          fetchMeters();
        }
        prevPending.current = p;
      } catch (e) {}
    }, 5000);
    return () => clearInterval(id);
  }, [fetchMeters]);

  const filtered = useMemo(() => {
    return meters.filter((m) => {
      if (!visible[m.statut]) return false; // hide by Excel status
      if (m.statut_app && !visible[m.statut_app]) return false; // hide by app status
      if (search) {
        const q = search.toLowerCase();
        const hay = `${m.ref || ""} ${m.nom_famille || ""} ${m.numero_serie || ""} ${m.rue || ""} ${m.lieu || ""} ${m.code_postal || ""} ${m.lot || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [meters, visible, search]);

  const panelMeters = selection ? selection.filter((m) => filtered.some((f) => f.id === m.id)) : filtered;

  const onUpdate = (updated) => {
    setMeters((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    setSelection((prev) => (prev ? prev.map((m) => (m.id === updated.id ? updated : m)) : prev));
  };

  const statusCounts = useMemo(() => {
    const c = {};
    STATUS_ORDER.forEach((k) => (c[k] = 0));
    meters.forEach((m) => {
      if (m.statut) c[m.statut] = (c[m.statut] || 0) + 1;
      if (m.statut_app) c[m.statut_app] = (c[m.statut_app] || 0) + 1;
    });
    return c;
  }, [meters]);

  const toggleStatus = (k) => setVisible((v) => ({ ...v, [k]: !v[k] }));

  return (
    <div className="h-screen flex flex-col bg-[#0B0F17] text-slate-100 overflow-hidden">
      {/* Navbar */}
      <header className="h-16 border-b border-slate-800 bg-slate-900/95 backdrop-blur-md px-4 lg:px-6 flex items-center justify-between z-[1000] shrink-0">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-[#00A896] flex items-center justify-center">
            <Zap className="h-5 w-5 text-[#04141a]" strokeWidth={2.5} />
          </div>
          <div className="hidden sm:block">
            <p className="font-display font-extrabold text-sm leading-none">SIG MAP</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Planification compteurs</p>
          </div>
          <nav className="flex items-center gap-1 ml-2 lg:ml-6 bg-slate-950/60 rounded-lg p-1 border border-slate-800">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`nav-tab-${t.key}`}
                className={`flex items-center gap-1.5 px-2.5 lg:px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  tab === t.key ? "bg-[#00A896] text-[#04141a]" : "text-slate-400 hover:text-slate-100"
                }`}
              >
                <t.icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          {pending > 0 && (
            <span className="hidden md:flex items-center gap-1.5 text-xs text-amber-400" data-testid="geocode-pending">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Géocodage… {pending}
            </span>
          )}
          <Button size="sm" onClick={() => setImportMode("import")} data-testid="excel-upload-button" className="h-9 bg-[#00A896] hover:bg-[#028090] text-[#04141a] font-semibold">
            <UploadCloud className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Ajouter</span>
          </Button>
          <div className="hidden md:flex items-center gap-2 pl-2 border-l border-slate-800">
            <div className="h-8 w-8 rounded-full bg-slate-800 flex items-center justify-center text-xs font-semibold text-[#00A896]">
              {(user?.username || "?").slice(0, 2).toUpperCase()}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" data-testid="settings-button" className="h-9 text-slate-400 hover:text-slate-100" title="Paramètres">
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[1200] bg-slate-900 border-slate-700 w-60">
              <DropdownMenuLabel className="text-xs text-slate-400">Paramètres</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem onClick={handleRegeocode} data-testid="regeocode-action" className="text-sm text-slate-200 focus:bg-slate-800 cursor-pointer">
                <Radar className="h-4 w-4 mr-2 text-[#00A896]" /> Re-scanner les positions
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setResetOpen(true)} data-testid="reset-action" className="text-sm text-rose-400 focus:bg-slate-800 focus:text-rose-300 cursor-pointer">
                <Trash2 className="h-4 w-4 mr-2" /> Réinitialiser les données
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="ghost" onClick={logout} data-testid="logout-button" className="h-9 text-slate-400 hover:text-rose-400">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Filters bar */}
      {tab !== "stats" && (
        <div className="min-h-14 py-2 border-b border-slate-800 bg-slate-900/50 px-4 lg:px-6 flex flex-wrap items-center gap-3 shrink-0">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher ID, adresse, lieu…" data-testid="search-input" className="pl-9 h-9 bg-slate-950/60 border-slate-700 text-sm" />
          </div>

          {/* Status visibility dropdowns */}
          <div className="flex items-center gap-2">
            <StatusFilterDropdown
              label="Statuts Excel"
              keys={EXCEL_STATUSES}
              visible={visible}
              onToggle={toggleStatus}
              counts={statusCounts}
              testid="excel-status-filter"
            />
            <StatusFilterDropdown
              label="Statuts app"
              keys={APP_STATUSES}
              visible={visible}
              onToggle={toggleStatus}
              counts={statusCounts}
              testid="app-status-filter"
            />
          </div>

          <span className="text-xs text-slate-500 hidden lg:block ml-auto" data-testid="total-count">
            {filtered.length} / {meters.length} affichés
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={filtered.length === 0}
            onClick={() => {
              exportMetersToExcel(filtered);
              toast.success(`${filtered.length} compteur(s) exporté(s)`);
            }}
            data-testid="export-excel-button"
            className="h-9 border-slate-700 bg-slate-950/60 text-slate-200 hover:bg-slate-800 hover:text-white"
          >
            <Download className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Exporter Excel</span>
          </Button>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 overflow-hidden">
        {loading ? (
          <div className="h-full flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : tab === "map" ? (
          <div className="h-full flex flex-col md:flex-row">
            <div className="flex-1 relative min-h-[300px]">
              <MapView meters={filtered} onSelect={(list) => setSelection(list)} />
            </div>
            <div className="w-full md:w-[420px] lg:w-[460px] h-1/2 md:h-full border-t md:border-t-0 md:border-l border-slate-800 bg-slate-900/90 shrink-0">
              <MeterPanel
                meters={panelMeters}
                title={selection ? "Zone sélectionnée" : "Tous les compteurs"}
                onUpdate={onUpdate}
                onClear={() => setSelection(null)}
                showClear={!!selection}
              />
            </div>
          </div>
        ) : tab === "table" ? (
          <div className="h-full overflow-y-auto">
            <MeterTable meters={filtered} />
          </div>
        ) : (
          <StatsView />
        )}
      </main>

      <Footer />

      <ImportModal open={importMode !== null} mode={importMode || "import"} onOpenChange={(o) => setImportMode(o ? importMode : null)} onDone={fetchMeters} />

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent className="bg-slate-900 border-slate-700 text-slate-100 z-[2000]">
          <AlertDialogHeader>
            <AlertDialogTitle>Réinitialiser toutes les données ?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Cette action supprime définitivement <strong>tous les compteurs</strong> de la base. Elle est irréversible. Vous pourrez ensuite réimporter vos fichiers Excel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel" className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-confirm"
              onClick={(e) => { e.preventDefault(); handleReset(); }}
              disabled={resetting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Tout supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
